import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { LogOut, Plus, Trash2, Edit, Upload, CheckCircle } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  
  // States for data
  const [stores, setStores] = useState([]);
  const [offers, setOffers] = useState([]);
  const [collections, setCollections] = useState([]);
  const [photos, setPhotos] = useState([]);
  
  // Form states
  const [showStoreForm, setShowStoreForm] = useState(false);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showPhotoForm, setShowPhotoForm] = useState(false);
  const [showCollectionForm, setShowCollectionForm] = useState(false);
  
  const [currentStore, setCurrentStore] = useState(null);
  const [currentOffer, setCurrentOffer] = useState(null);
  const [currentCollection, setCurrentCollection] = useState(null);

  // Push to production states
  const [selectedItems, setSelectedItems] = useState({
    stores: [],
    offers: [],
    collections: [],
    photos: []
  });
  const [pushingToProduction, setPushingToProduction] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    const adminUsername = localStorage.getItem('admin_username');
    
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
    
    setToken(adminToken);
    setUsername(adminUsername);
    fetchAllData(adminToken);
  }, [navigate]);

  const fetchAllData = async (authToken) => {
    try {
      const headers = {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      };

      const [storesRes, offersRes, collectionsRes, photosRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stores`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/offers`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/collections`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/photos`, { headers }),
      ]);

      if (storesRes.ok) setStores(await storesRes.json());
      if (offersRes.ok) setOffers(await offersRes.json());
      if (collectionsRes.ok) setCollections(await collectionsRes.json());
      if (photosRes.ok) setPhotos(await photosRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const apiCall = async (url, method, data = null) => {
    const options = {
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
    
    if (data) {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, options);
    
    if (!response.ok) {
      throw new Error('API call failed');
    }
    
    return method !== 'DELETE' ? await response.json() : null;
  };

  // ==================== OFFERS MANAGEMENT ====================
  
  const handleOfferSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const offerData = {
      title: formData.get('title'),
      description: formData.get('description'),
      badge: formData.get('badge') || 'Limited Time',
      cta: formData.get('cta') || 'Visit Store',
      valid_until: formData.get('valid_until') || null,
    };

    try {
      if (currentOffer) {
        await apiCall(`/api/admin/offers/${currentOffer.id}`, 'PUT', offerData);
      } else {
        await apiCall('/api/admin/offers', 'POST', offerData);
      }
      await fetchAllData(token);
      setShowOfferForm(false);
      setCurrentOffer(null);
      e.target.reset();
    } catch (error) {
      alert('Error saving offer');
    }
  };

  const handleDeleteOffer = async (id) => {
    if (!window.confirm('Delete this offer?')) return;
    try {
      await apiCall(`/api/admin/offers/${id}`, 'DELETE');
      await fetchAllData(token);
    } catch (error) {
      alert('Error deleting offer');
    }
  };

  // ==================== PHOTOS MANAGEMENT ====================
  
  const handlePhotoSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const photoData = {
      url: formData.get('url'),
      alt: formData.get('alt'),
      category: formData.get('category') || 'product',
    };

    try {
      await apiCall('/api/admin/photos', 'POST', photoData);
      await fetchAllData(token);
      setShowPhotoForm(false);
      e.target.reset();
    } catch (error) {
      alert('Error adding photo');
    }
  };

  const handleDeletePhoto = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await apiCall(`/api/admin/photos/${id}`, 'DELETE');
      await fetchAllData(token);
    } catch (error) {
      alert('Error deleting photo');
    }
  };

  // ==================== PUSH TO PRODUCTION ====================
  
  const handleSelectItem = (type, id, checked) => {
    setSelectedItems(prev => ({
      ...prev,
      [type]: checked 
        ? [...prev[type], id]
        : prev[type].filter(itemId => itemId !== id)
    }));
  };

  const handleSelectAll = (type, items) => {
    const allIds = items.map(item => item.id);
    const allSelected = allIds.every(id => selectedItems[type].includes(id));
    
    setSelectedItems(prev => ({
      ...prev,
      [type]: allSelected ? [] : allIds
    }));
  };

  const handlePushToProduction = async () => {
    const totalSelected = 
      selectedItems.stores.length + 
      selectedItems.offers.length + 
      selectedItems.collections.length + 
      selectedItems.photos.length;

    if (totalSelected === 0) {
      alert('Please select at least one item to push to production');
      return;
    }

    const confirmMessage = `Push ${totalSelected} item(s) to PRODUCTION?\n\n` +
      `Stores: ${selectedItems.stores.length}\n` +
      `Collections: ${selectedItems.collections.length}\n` +
      `Offers: ${selectedItems.offers.length}\n` +
      `Photos: ${selectedItems.photos.length}\n\n` +
      `This will make changes LIVE on epicfashion.net`;

    if (!window.confirm(confirmMessage)) return;

    // Get admin password for production authentication
    const adminPassword = prompt('Enter admin password to authenticate with production:');
    if (!adminPassword) {
      alert('Password is required to push to production');
      return;
    }

    setPushingToProduction(true);
    setPushResult(null);

    try {
      const response = await apiCall('/api/admin/push-to-production', 'POST', {
        production_url: process.env.REACT_APP_PRODUCTION_URL || 'https://epicfashion.net',
        admin_password: adminPassword,
        items: selectedItems
      });

      setPushResult({
        success: true,
        ...response
      });

      // Clear selections after successful push
      setSelectedItems({
        stores: [],
        offers: [],
        collections: [],
        photos: []
      });

      alert(`Successfully pushed to production!\n\nStores: ${response.pushed.stores}\nCollections: ${response.pushed.collections}\nOffers: ${response.pushed.offers}\nPhotos: ${response.pushed.photos}`);

    } catch (error) {
      setPushResult({
        success: false,
        error: error.message
      });
      alert('Error pushing to production: ' + error.message);
    } finally {
      setPushingToProduction(false);
    }
  };

  // ==================== STORES MANAGEMENT ====================
  
  const handleStoreSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const storeData = {
      name: formData.get('name'),
      address: formData.get('address'),
      phone: formData.get('phone'),
      whatsapp: formData.get('whatsapp'),
      maps_url: formData.get('maps_url'),
      type: formData.get('type'),
      timings: formData.get('timings') || 'Open 365 Days | 10:30 AM - 9:30 PM',
    };

    try {
      if (currentStore) {
        await apiCall(`/api/admin/stores/${currentStore.id}`, 'PUT', storeData);
      } else {
        await apiCall('/api/admin/stores', 'POST', storeData);
      }
      await fetchAllData(token);
      setShowStoreForm(false);
      setCurrentStore(null);
      e.target.reset();
    } catch (error) {
      alert('Error saving store');
    }
  };

  const handleDeleteStore = async (id) => {
    if (!window.confirm('Delete this store?')) return;
    try {
      await apiCall(`/api/admin/stores/${id}`, 'DELETE');
      await fetchAllData(token);
    } catch (error) {
      alert('Error deleting store');
    }
  };

  // ==================== COLLECTIONS MANAGEMENT ====================
  
  const handleCollectionSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const collectionData = {
      title: formData.get('title'),
      description: formData.get('description'),
      image: formData.get('image'),
      category: formData.get('category') || 'Fashion',
      brand: formData.get('brand') || null,
    };

    try {
      if (currentCollection) {
        await apiCall(`/api/admin/collections/${currentCollection.id}`, 'PUT', collectionData);
      } else {
        await apiCall('/api/admin/collections', 'POST', collectionData);
      }
      await fetchAllData(token);
      setShowCollectionForm(false);
      setCurrentCollection(null);
      e.target.reset();
    } catch (error) {
      alert('Error saving collection');
    }
  };

  const handleDeleteCollection = async (id) => {
    if (!window.confirm('Delete this collection?')) return;
    try {
      await apiCall(`/api/admin/collections/${id}`, 'DELETE');
      await fetchAllData(token);
    } catch (error) {
      alert('Error deleting collection');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-white via-soft-gray to-cream-white">
      {/* Header */}
      <header className="bg-white border-b border-soft-gray sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-warm-sage flex items-center justify-center">
                <span className="text-cream-white font-serif text-xl font-medium">E</span>
              </div>
              <div>
                <h1 className="text-xl font-serif text-charcoal">EPIC Admin</h1>
                <p className="text-xs text-warm-gray">Welcome, {username}</p>
              </div>
            </div>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="border-charcoal text-charcoal hover:bg-charcoal hover:text-cream-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {/* Show staging banner if in staging environment */}
        {process.env.REACT_APP_IS_STAGING === 'true' && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>📝 Staging Environment:</strong> You're in the preview site. Changes here won't affect the live website until you push them to production.
            </p>
          </div>
        )}

        <Tabs defaultValue="offers" className="w-full">
          <TabsList className={`grid w-full ${process.env.REACT_APP_IS_STAGING === 'true' ? 'grid-cols-5' : 'grid-cols-4'} mb-8`}>
            <TabsTrigger value="offers">Offers</TabsTrigger>
            <TabsTrigger value="photos">Product Photos</TabsTrigger>
            <TabsTrigger value="collections">Collections</TabsTrigger>
            <TabsTrigger value="stores">Stores</TabsTrigger>
            {process.env.REACT_APP_IS_STAGING === 'true' && (
              <TabsTrigger value="push" className="bg-green-50 data-[state=active]:bg-green-100">
                <Upload className="w-4 h-4 mr-2 inline" />
                Push to Live
              </TabsTrigger>
            )}
          </TabsList>

          {/* OFFERS TAB */}
          <TabsContent value="offers">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Manage Offers</CardTitle>
                  <Button 
                    onClick={() => {
                      setShowOfferForm(!showOfferForm);
                      setCurrentOffer(null);
                    }}
                    className="bg-warm-sage hover:bg-warm-sage/90"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Offer
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showOfferForm && (
                  <form onSubmit={handleOfferSubmit} className="mb-6 p-4 bg-soft-gray rounded-lg space-y-4">
                    <div>
                      <Label htmlFor="offer-title">Title *</Label>
                      <Input 
                        id="offer-title" 
                        name="title" 
                        defaultValue={currentOffer?.title} 
                        required 
                        placeholder="e.g., Winter Sale - 30% Off"
                      />
                    </div>
                    <div>
                      <Label htmlFor="offer-description">Description *</Label>
                      <Textarea 
                        id="offer-description" 
                        name="description" 
                        defaultValue={currentOffer?.description} 
                        required 
                        placeholder="Describe the offer details"
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="offer-badge">Badge</Label>
                        <Input 
                          id="offer-badge" 
                          name="badge" 
                          defaultValue={currentOffer?.badge || 'Limited Time'} 
                          placeholder="e.g., Hot Deal"
                        />
                      </div>
                      <div>
                        <Label htmlFor="offer-cta">Call to Action</Label>
                        <Input 
                          id="offer-cta" 
                          name="cta" 
                          defaultValue={currentOffer?.cta || 'Visit Store'} 
                          placeholder="e.g., Shop Now"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="offer-valid">Valid Until (optional)</Label>
                      <Input 
                        id="offer-valid" 
                        name="valid_until" 
                        defaultValue={currentOffer?.valid_until} 
                        placeholder="e.g., Dec 31, 2023"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="bg-warm-sage hover:bg-warm-sage/90">
                        {currentOffer ? 'Update' : 'Add'} Offer
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setShowOfferForm(false);
                          setCurrentOffer(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}

                <div className="space-y-4">
                  {offers.length === 0 ? (
                    <p className="text-center text-warm-gray py-8">No offers yet. Add your first offer!</p>
                  ) : (
                    offers.map((offer) => (
                      <div key={offer.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-medium text-lg">{offer.title}</h3>
                              <span className="text-xs bg-warm-sage/20 text-warm-sage px-2 py-1 rounded">{offer.badge}</span>
                            </div>
                            <p className="text-sm text-warm-gray mb-2">{offer.description}</p>
                            {offer.valid_until && (
                              <p className="text-xs text-warm-gray">Valid until: {offer.valid_until}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setCurrentOffer(offer);
                                setShowOfferForm(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDeleteOffer(offer.id)}
                              className="text-red-500 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PHOTOS TAB */}
          <TabsContent value="photos">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Manage Product Photos</CardTitle>
                  <Button 
                    onClick={() => setShowPhotoForm(!showPhotoForm)}
                    className="bg-warm-sage hover:bg-warm-sage/90"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Photo
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showPhotoForm && (
                  <form onSubmit={handlePhotoSubmit} className="mb-6 p-4 bg-soft-gray rounded-lg space-y-4">
                    <div>
                      <Label htmlFor="photo-url">Photo URL *</Label>
                      <Input 
                        id="photo-url" 
                        name="url" 
                        required 
                        placeholder="https://example.com/photo.jpg"
                      />
                      <p className="text-xs text-warm-gray mt-1">Upload photo to a hosting service (Imgur, Cloudinary) and paste URL here</p>
                    </div>
                    <div>
                      <Label htmlFor="photo-alt">Description *</Label>
                      <Input 
                        id="photo-alt" 
                        name="alt" 
                        required 
                        placeholder="e.g., Blue Denim Jacket - Jack & Jones"
                      />
                    </div>
                    <div>
                      <Label htmlFor="photo-category">Category</Label>
                      <Input 
                        id="photo-category" 
                        name="category" 
                        defaultValue="product" 
                        placeholder="e.g., product, lifestyle, store"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="bg-warm-sage hover:bg-warm-sage/90">
                        Add Photo
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setShowPhotoForm(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {photos.length === 0 ? (
                    <p className="col-span-full text-center text-warm-gray py-8">No photos yet. Add your first photo!</p>
                  ) : (
                    photos.map((photo) => (
                      <div key={photo.id} className="relative group">
                        <img 
                          src={photo.url} 
                          alt={photo.alt} 
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleDeletePhoto(photo.id)}
                            className="bg-white text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-warm-gray mt-2 truncate">{photo.alt}</p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* COLLECTIONS TAB */}
          <TabsContent value="collections">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Manage Collections</CardTitle>
                  <Button 
                    onClick={() => {
                      setShowCollectionForm(!showCollectionForm);
                      setCurrentCollection(null);
                    }}
                    className="bg-warm-sage hover:bg-warm-sage/90"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Collection
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showCollectionForm && (
                  <form onSubmit={handleCollectionSubmit} className="mb-6 p-4 bg-soft-gray rounded-lg space-y-4">
                    <div>
                      <Label htmlFor="collection-title">Title *</Label>
                      <Input 
                        id="collection-title" 
                        name="title" 
                        defaultValue={currentCollection?.title} 
                        required 
                        placeholder="e.g., Spring Collection 2024"
                      />
                    </div>
                    <div>
                      <Label htmlFor="collection-description">Description *</Label>
                      <Textarea 
                        id="collection-description" 
                        name="description" 
                        defaultValue={currentCollection?.description} 
                        required 
                        placeholder="Describe the collection"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label htmlFor="collection-image">Image URL *</Label>
                      <Input 
                        id="collection-image" 
                        name="image" 
                        defaultValue={currentCollection?.image} 
                        required 
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="collection-category">Category</Label>
                        <Input 
                          id="collection-category" 
                          name="category" 
                          defaultValue={currentCollection?.category || 'Fashion'} 
                          placeholder="e.g., Casual, Formal"
                        />
                      </div>
                      <div>
                        <Label htmlFor="collection-brand">Brand</Label>
                        <Input 
                          id="collection-brand" 
                          name="brand" 
                          defaultValue={currentCollection?.brand} 
                          placeholder="e.g., Jack & Jones"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="bg-warm-sage hover:bg-warm-sage/90">
                        {currentCollection ? 'Update' : 'Add'} Collection
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setShowCollectionForm(false);
                          setCurrentCollection(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  {collections.length === 0 ? (
                    <p className="col-span-full text-center text-warm-gray py-8">No collections yet. Add your first collection!</p>
                  ) : (
                    collections.map((collection) => (
                      <div key={collection.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                        <img 
                          src={collection.image} 
                          alt={collection.title} 
                          className="w-full h-48 object-cover"
                        />
                        <div className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h3 className="font-medium">{collection.title}</h3>
                              {collection.brand && (
                                <p className="text-xs text-warm-sage">{collection.brand}</p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setCurrentCollection(collection);
                                  setShowCollectionForm(true);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleDeleteCollection(collection.id)}
                                className="text-red-500 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-sm text-warm-gray">{collection.description}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* STORES TAB */}
          <TabsContent value="stores">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Manage Store Locations</CardTitle>
                  <Button 
                    onClick={() => {
                      setShowStoreForm(!showStoreForm);
                      setCurrentStore(null);
                    }}
                    className="bg-warm-sage hover:bg-warm-sage/90"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Store
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showStoreForm && (
                  <form onSubmit={handleStoreSubmit} className="mb-6 p-4 bg-soft-gray rounded-lg space-y-4">
                    <div>
                      <Label htmlFor="store-name">Store Name *</Label>
                      <Input 
                        id="store-name" 
                        name="name" 
                        defaultValue={currentStore?.name} 
                        required 
                        placeholder="e.g., Karimnagar"
                      />
                    </div>
                    <div>
                      <Label htmlFor="store-address">Address *</Label>
                      <Input 
                        id="store-address" 
                        name="address" 
                        defaultValue={currentStore?.address} 
                        required 
                        placeholder="Full address"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="store-phone">Phone *</Label>
                        <Input 
                          id="store-phone" 
                          name="phone" 
                          defaultValue={currentStore?.phone} 
                          required 
                          placeholder="+91 XXXXX XXXXX"
                        />
                      </div>
                      <div>
                        <Label htmlFor="store-whatsapp">WhatsApp Number *</Label>
                        <Input 
                          id="store-whatsapp" 
                          name="whatsapp" 
                          defaultValue={currentStore?.whatsapp} 
                          required 
                          placeholder="91XXXXXXXXXX"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="store-maps">Google Maps URL *</Label>
                      <Input 
                        id="store-maps" 
                        name="maps_url" 
                        defaultValue={currentStore?.maps_url} 
                        required 
                        placeholder="https://maps.app.goo.gl/..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="store-type">Store Type *</Label>
                        <Input 
                          id="store-type" 
                          name="type" 
                          defaultValue={currentStore?.type} 
                          required 
                          placeholder="e.g., Multi-Brand Outlet"
                        />
                      </div>
                      <div>
                        <Label htmlFor="store-timings">Timings</Label>
                        <Input 
                          id="store-timings" 
                          name="timings" 
                          defaultValue={currentStore?.timings || 'Open 365 Days | 10:30 AM - 9:30 PM'} 
                          placeholder="e.g., 10:00 AM - 9:00 PM"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="bg-warm-sage hover:bg-warm-sage/90">
                        {currentStore ? 'Update' : 'Add'} Store
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setShowStoreForm(false);
                          setCurrentStore(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}

                <div className="space-y-4">
                  {stores.length === 0 ? (
                    <p className="text-center text-warm-gray py-8">No stores yet. Add your first store!</p>
                  ) : (
                    stores.map((store) => (
                      <div key={store.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-medium text-lg">{store.name}</h3>
                            <p className="text-sm text-warm-gray mb-1">{store.address}</p>
                            <p className="text-sm text-warm-gray mb-1">Phone: {store.phone}</p>
                            <p className="text-sm text-warm-gray mb-1">WhatsApp: {store.whatsapp}</p>
                            <p className="text-xs text-warm-sage">{store.type}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setCurrentStore(store);
                                setShowStoreForm(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDeleteStore(store.id)}
                              className="text-red-500 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PUSH TO PRODUCTION TAB - Only visible in staging */}
          {process.env.REACT_APP_IS_STAGING === 'true' && (
            <TabsContent value="push">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Push Changes to Production
                  </CardTitle>
                  <p className="text-sm text-warm-gray mt-2">
                    Select the items you want to push live to <strong>epicfashion.net</strong>. 
                    Only selected items will be updated on the production website.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    
                    {/* Offers Section */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-lg">Offers ({offers.length})</h3>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSelectAll('offers', offers)}
                        >
                          {selectedItems.offers.length === offers.length ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {offers.length === 0 ? (
                          <p className="text-sm text-warm-gray">No offers to push</p>
                        ) : (
                          offers.map(offer => (
                            <div key={offer.id} className="flex items-start gap-3 p-3 hover:bg-soft-gray rounded">
                              <Checkbox
                                checked={selectedItems.offers.includes(offer.id)}
                                onCheckedChange={(checked) => handleSelectItem('offers', offer.id, checked)}
                              />
                              <div className="flex-1">
                                <p className="font-medium">{offer.title}</p>
                                <p className="text-sm text-warm-gray">{offer.description}</p>
                                {offer.badge && (
                                  <span className="inline-block mt-1 text-xs px-2 py-1 bg-warm-sage/20 text-warm-sage rounded">
                                    {offer.badge}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Collections Section */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-lg">Collections ({collections.length})</h3>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSelectAll('collections', collections)}
                        >
                          {selectedItems.collections.length === collections.length ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {collections.length === 0 ? (
                          <p className="text-sm text-warm-gray">No collections to push</p>
                        ) : (
                          collections.map(collection => (
                            <div key={collection.id} className="flex items-start gap-3 p-3 hover:bg-soft-gray rounded">
                              <Checkbox
                                checked={selectedItems.collections.includes(collection.id)}
                                onCheckedChange={(checked) => handleSelectItem('collections', collection.id, checked)}
                              />
                              <div className="flex-1">
                                <p className="font-medium">{collection.title}</p>
                                <p className="text-sm text-warm-gray">{collection.description}</p>
                                <span className="text-xs text-warm-sage">{collection.brand}</span>
                              </div>
                              {collection.image && (
                                <img src={collection.image} alt={collection.title} className="w-16 h-16 object-cover rounded" />
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Stores Section */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-lg">Stores ({stores.length})</h3>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSelectAll('stores', stores)}
                        >
                          {selectedItems.stores.length === stores.length ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {stores.length === 0 ? (
                          <p className="text-sm text-warm-gray">No stores to push</p>
                        ) : (
                          stores.map(store => (
                            <div key={store.id} className="flex items-start gap-3 p-3 hover:bg-soft-gray rounded">
                              <Checkbox
                                checked={selectedItems.stores.includes(store.id)}
                                onCheckedChange={(checked) => handleSelectItem('stores', store.id, checked)}
                              />
                              <div className="flex-1">
                                <p className="font-medium">{store.name}</p>
                                <p className="text-sm text-warm-gray">{store.address}</p>
                                <p className="text-xs text-warm-sage">{store.type}</p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Photos Section */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-lg">Store Photos ({photos.length})</h3>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSelectAll('photos', photos)}
                        >
                          {selectedItems.photos.length === photos.length ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {photos.length === 0 ? (
                          <p className="text-sm text-warm-gray col-span-full">No photos to push</p>
                        ) : (
                          photos.map(photo => (
                            <div key={photo.id} className="relative group">
                              <div className="absolute top-2 left-2 z-10">
                                <Checkbox
                                  checked={selectedItems.photos.includes(photo.id)}
                                  onCheckedChange={(checked) => handleSelectItem('photos', photo.id, checked)}
                                  className="bg-white"
                                />
                              </div>
                              <img 
                                src={photo.url || photo.image} 
                                alt={photo.alt} 
                                className="w-full h-32 object-cover rounded group-hover:opacity-75 transition-opacity"
                              />
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Push Button */}
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-lg mb-1">Ready to Push?</h4>
                          <p className="text-sm text-warm-gray">
                            {selectedItems.stores.length + selectedItems.offers.length + selectedItems.collections.length + selectedItems.photos.length} item(s) selected
                          </p>
                        </div>
                        <Button
                          size="lg"
                          onClick={handlePushToProduction}
                          disabled={pushingToProduction || (
                            selectedItems.stores.length === 0 &&
                            selectedItems.offers.length === 0 &&
                            selectedItems.collections.length === 0 &&
                            selectedItems.photos.length === 0
                          )}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          {pushingToProduction ? (
                            <>Pushing...</>
                          ) : (
                            <>
                              <CheckCircle className="w-5 h-5 mr-2" />
                              Push to Production
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Result Message */}
                    {pushResult && (
                      <div className={`p-4 rounded-lg ${pushResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <p className={`font-medium ${pushResult.success ? 'text-green-900' : 'text-red-900'}`}>
                          {pushResult.success ? '✅ Successfully pushed to production!' : '❌ Error pushing to production'}
                        </p>
                        {pushResult.success && pushResult.pushed && (
                          <div className="mt-2 text-sm text-green-800">
                            <p>Stores: {pushResult.pushed.stores}</p>
                            <p>Collections: {pushResult.pushed.collections}</p>
                            <p>Offers: {pushResult.pushed.offers}</p>
                            <p>Photos: {pushResult.pushed.photos}</p>
                          </div>
                        )}
                        {pushResult.errors && pushResult.errors.length > 0 && (
                          <div className="mt-2 text-sm text-red-800">
                            {pushResult.errors.map((err, idx) => (
                              <p key={idx}>• {err}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
